import React from 'react';
import { useStore } from '../store/useStore';
import AdminUsersPanel from './AdminUsersPanel';
import styles from './AdminConsole.module.css';

// Org-level admin surface. Single page today (Users), but the shell
// is set up the same way as ProjectLayout: left-nav + content area,
// driven by `adminPage` in the store. New sections drop into NAV +
// the `panel` map below — no other plumbing required.
//
// The component is admin-only at the UI level (via AdminUsersPanel's
// own role check + the TopBar entry point that's hidden for
// non-admins). The server is the actual gate: every /api/users/*
// mutation requires `requireAdmin`. Don't trust this UI guard alone.

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M2 17c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M14 8a3 3 0 000-6M18 17c0-2.761-1.79-5.1-4.274-5.834"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M11 4l-6 6 6 6M5 10h13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const NAV = [
  { id: 'users', label: 'Users', Icon: UsersIcon },
  // Add future admin sections here. Each id keys into the `panel`
  // map below; AdminConsole stays a thin shell.
];

export default function AdminConsole() {
  const { adminPage, setAdminPage, role, setView, setProject } = useStore();

  const goToProjects = () => {
    setProject(null);
    setView('projects');
  };

  // Belt-and-braces gate. The server already 403s non-admins on the
  // user-management endpoints, but rendering the console for a
  // non-admin would just splash 403 toasts; show a clear message
  // instead and keep them out of the surface.
  if (role !== 'admin') {
    return (
      <div className={styles.layout}>
        <div className={styles.content} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: 'var(--text-faint)', fontSize: 13 }}>
            Admins only.
          </div>
        </div>
      </div>
    );
  }

  const panel = {
    users: <AdminUsersPanel />,
  }[adminPage] ?? <AdminUsersPanel />;

  return (
    <div className={styles.layout}>
      <nav className={styles.nav}>
        <div className={styles.navHeader}>ADMIN CONSOLE</div>
        {NAV.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={`${styles.navItem} ${adminPage === id ? styles.navActive : ''}`}
            onClick={() => setAdminPage(id)}
            aria-label={label}
          >
            <Icon />
            <span className={styles.navLabel}>{label}</span>
          </button>
        ))}
        {/* Bottom-anchored exit so the user can leave the admin
            surface without going back to the role-menu dropdown. */}
        <div style={{ flex: 1 }} />
        <button
          className={styles.navItem}
          onClick={goToProjects}
          aria-label="Back to Projects"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <BackIcon />
          <span className={styles.navLabel}>Back to Projects</span>
        </button>
      </nav>

      <div className={styles.content}>
        {panel}
      </div>
    </div>
  );
}
